// Multi-Strategy Media Prefetcher with comprehensive error handling
// 4 Prefetch Strategies: Cache API → link prefetch → Image/Video preload → Fetch fallback

class MediaPrefetcher {
  constructor() {
    this.cache = null
    this.prefetchQueue = []
    this.isPrefetching = false
    this.CACHE_NAME = 'ampthill-prefetch-v2'
    this.maxConcurrent = {
      critical: 1,
      high: 3,
      low: 5,
    }
    this.activePrefetches = {
      critical: 0,
      high: 0,
      low: 0,
    }
    this.prefetchStats = {
      success: 0,
      failed: 0,
      cached: 0,
    }
    this.init()
  }

  async init() {
    try {
      if ('caches' in window) {
        this.cache = await caches.open(this.CACHE_NAME)
      }
    } catch (e) {
      // Cache API not available, continue without it
      this.cache = null
    }
  }

  /**
   * Strategy 1: Cache API prefetch
   * Skip for Firebase Storage URLs to avoid CORS errors
   */
  async prefetchWithCacheAPI(url) {
    if (!this.cache) return false
    
    // Skip Cache API for Firebase Storage URLs - they don't need CORS and Cache API triggers CORS checks
    if (this.isFirebaseStorageUrl(url)) {
      return false
    }

    try {
      // Check if already cached
      const cached = await this.cache.match(url)
      if (cached) {
        this.prefetchStats.cached++
        return true
      }

      // Fetch and cache - only for non-Firebase URLs
      const response = await fetch(url, { method: 'GET' })
      if (response.ok) {
        await this.cache.put(url, response.clone())
        this.prefetchStats.success++
        return true
      }
      return false
    } catch (e) {
      return false
    }
  }

  /**
   * Check if URL is from Firebase Storage (doesn't need crossOrigin)
   * Handles both old and new Firebase Storage URL formats
   */
  isFirebaseStorageUrl(url) {
    if (!url || typeof url !== 'string') return false
    try {
      // Check for Firebase Storage URL patterns
      return (
        url.includes('firebasestorage.googleapis.com') ||
        url.includes('firebasestorage.app') ||
        url.includes('storage.googleapis.com') ||
        // Also check for the specific bucket pattern
        url.includes('ampthill-77f7f.firebasestorage.app')
      )
    } catch (e) {
      return false
    }
  }

  /**
   * Strategy 2: link rel=prefetch
   * Skip for Firebase Storage URLs - they work better with direct element loading
   */
  async prefetchWithLink(url, type = 'image') {
    // Skip link prefetch for Firebase Storage URLs - use element prefetch instead
    if (this.isFirebaseStorageUrl(url)) {
      return false
    }
    
    return new Promise((resolve) => {
      try {
        // Check if link already exists
        const existingLink = document.querySelector(`link[href="${url}"]`)
        if (existingLink) {
          resolve(true)
          return
        }

        const link = document.createElement('link')
        link.rel = 'prefetch'
        link.as = type === 'video' ? 'video' : 'image'
        link.href = url
        // Only set crossOrigin for non-Firebase Storage URLs to avoid CORS errors
        if (!this.isFirebaseStorageUrl(url)) {
          link.crossOrigin = 'anonymous'
        }

        const onLoad = () => {
          cleanup()
          this.prefetchStats.success++
          resolve(true)
        }

        const onError = () => {
          cleanup()
          resolve(false)
        }

        const cleanup = () => {
          link.removeEventListener('load', onLoad)
          link.removeEventListener('error', onError)
        }

        link.addEventListener('load', onLoad)
        link.addEventListener('error', onError)

        document.head.appendChild(link)

        // Timeout after 10 seconds
        setTimeout(() => {
          cleanup()
          if (document.head.contains(link)) {
            link.remove()
          }
          resolve(false)
        }, 10000)
      } catch (e) {
        resolve(false)
      }
    })
  }

  /**
   * Strategy 3: Image/Video element preload (always available)
   */
  async prefetchWithElement(url, type = 'image') {
    return new Promise((resolve) => {
      try {
        if (type === 'video') {
          const video = document.createElement('video')
          video.preload = 'metadata'
          video.muted = true
          // Only set crossOrigin for non-Firebase Storage URLs to avoid CORS errors
          if (!this.isFirebaseStorageUrl(url)) {
            video.crossOrigin = 'anonymous'
          }

          const onCanPlay = () => {
            cleanup()
            this.prefetchStats.success++
            resolve(true)
          }

          const onError = () => {
            cleanup()
            resolve(false)
          }

          const cleanup = () => {
            video.removeEventListener('loadedmetadata', onCanPlay)
            video.removeEventListener('canplay', onCanPlay)
            video.removeEventListener('error', onError)
            video.src = ''
          }

          video.addEventListener('loadedmetadata', onCanPlay)
          video.addEventListener('canplay', onCanPlay)
          video.addEventListener('error', onError)
          video.src = url

          // Timeout after 10 seconds
          setTimeout(() => {
            cleanup()
            resolve(false)
          }, 10000)
        } else {
          const img = new Image()
          // Only set crossOrigin for non-Firebase Storage URLs to avoid CORS errors
          if (!this.isFirebaseStorageUrl(url)) {
            img.crossOrigin = 'anonymous'
          }

          const onLoad = () => {
            cleanup()
            this.prefetchStats.success++
            resolve(true)
          }

          const onError = () => {
            cleanup()
            resolve(false)
          }

          const cleanup = () => {
            img.onload = null
            img.onerror = null
            img.src = ''
          }

          img.onload = onLoad
          img.onerror = onError
          img.src = url

          // Timeout after 10 seconds
          setTimeout(() => {
            cleanup()
            resolve(false)
          }, 10000)
        }
      } catch (e) {
        resolve(false)
      }
    })
  }

  /**
   * Strategy 4: Fetch API with manual caching (fallback)
   * Skip for Firebase Storage URLs to avoid CORS errors
   */
  async prefetchWithFetch(url) {
    // Skip fetch for Firebase Storage URLs - use element prefetch instead
    if (this.isFirebaseStorageUrl(url)) {
      return false
    }
    
    try {
      const response = await fetch(url, { method: 'HEAD' })
      if (response.ok) {
        this.prefetchStats.success++
        return true
      }
      return false
    } catch (e) {
      return false
    }
  }

  /**
   * Prefetch image using all 4 strategies in order
   * For Firebase Storage URLs, skip Cache API, link prefetch, and fetch to avoid CORS
   */
  async prefetchImage(url, priority = 'high') {
    if (!url || typeof url !== 'string') return false

    const isFirebase = this.isFirebaseStorageUrl(url)
    
    // For Firebase Storage URLs, only use element prefetch (no CORS issues)
    if (isFirebase) {
      try {
        const result = await this.prefetchWithElement(url, 'image')
        if (result) return true
        this.prefetchStats.failed++
        return false
      } catch (e) {
        this.prefetchStats.failed++
        return false
      }
    }

    // For non-Firebase URLs, try all strategies in order
    const strategies = [
      () => this.prefetchWithCacheAPI(url),
      () => this.prefetchWithLink(url, 'image'),
      () => this.prefetchWithElement(url, 'image'),
      () => this.prefetchWithFetch(url),
    ]

    for (const strategy of strategies) {
      try {
        const result = await strategy()
        if (result) return true
      } catch (e) {
        // Try next strategy
        continue
      }
    }

    this.prefetchStats.failed++
    return false
  }

  /**
   * Prefetch video using all 4 strategies in order
   * For Firebase Storage URLs, skip Cache API, link prefetch, and fetch to avoid CORS
   */
  async prefetchVideo(url, priority = 'high') {
    if (!url || typeof url !== 'string') return false

    const isFirebase = this.isFirebaseStorageUrl(url)
    
    // For Firebase Storage URLs, only use element prefetch (no CORS issues)
    if (isFirebase) {
      try {
        const result = await this.prefetchWithElement(url, 'video')
        if (result) return true
        this.prefetchStats.failed++
        return false
      } catch (e) {
        this.prefetchStats.failed++
        return false
      }
    }

    // For non-Firebase URLs, try all strategies in order
    const strategies = [
      () => this.prefetchWithCacheAPI(url),
      () => this.prefetchWithLink(url, 'video'),
      () => this.prefetchWithElement(url, 'video'),
      () => this.prefetchWithFetch(url),
    ]

    for (const strategy of strategies) {
      try {
        const result = await strategy()
        if (result) return true
      } catch (e) {
        // Try next strategy
        continue
      }
    }

    this.prefetchStats.failed++
    return false
  }

  /**
   * Batch prefetch with priority queuing
   */
  async prefetchBatch(urls, type = 'image', priority = 'high') {
    if (!Array.isArray(urls) || urls.length === 0) return

    const maxConcurrent = this.maxConcurrent[priority] || 3
    const results = []

    // Process in batches
    for (let i = 0; i < urls.length; i += maxConcurrent) {
      const batch = urls.slice(i, i + maxConcurrent)
      const batchPromises = batch.map((url) =>
        type === 'video'
          ? this.prefetchVideo(url, priority)
          : this.prefetchImage(url, priority)
      )

      const batchResults = await Promise.allSettled(batchPromises)
      results.push(...batchResults)
    }

    return results
  }

  /**
   * Prefetch critical hero media immediately
   */
  async prefetchCritical(heroSlides) {
    if (!heroSlides || !Array.isArray(heroSlides) || heroSlides.length === 0) {
      return
    }

    const criticalUrls = []

    // Get first slide media (highest priority)
    const firstSlide = heroSlides[0]
    if (firstSlide?.backgroundVideo) {
      criticalUrls.push({ url: firstSlide.backgroundVideo, type: 'video' })
    } else if (firstSlide?.backgroundImage) {
      criticalUrls.push({ url: firstSlide.backgroundImage, type: 'image' })
    }

    // Get second slide for smooth carousel
    if (heroSlides.length > 1) {
      const secondSlide = heroSlides[1]
      if (
        secondSlide?.backgroundVideo &&
        secondSlide.backgroundVideo !== criticalUrls[0]?.url
      ) {
        criticalUrls.push({ url: secondSlide.backgroundVideo, type: 'video' })
      } else if (
        secondSlide?.backgroundImage &&
        secondSlide.backgroundImage !== criticalUrls[0]?.url
      ) {
        criticalUrls.push({ url: secondSlide.backgroundImage, type: 'image' })
      }
    }

    // Prefetch critical media with highest priority
    for (const item of criticalUrls) {
      try {
        if (item.type === 'video') {
          await this.prefetchVideo(item.url, 'critical')
        } else {
          await this.prefetchImage(item.url, 'critical')
        }
      } catch (e) {
        // Continue with next item
      }
    }
  }

  /**
   * Get prefetch statistics
   */
  getStats() {
    return { ...this.prefetchStats }
  }

  /**
   * Reset statistics
   */
  resetStats() {
    this.prefetchStats = {
      success: 0,
      failed: 0,
      cached: 0,
    }
  }
}

// Singleton instance
export const mediaPrefetcher = new MediaPrefetcher()
